/**
 * IIS / ASP.NET APIs often return **406 Not Acceptable** when `Accept` / caching / timing
 * don’t match content negotiation. Opening DevTools frequently enables “Disable cache” and slows
 * JS — both change behavior; this helper aligns cold loads closer to that path.
 */
const ACCEPT_JSON_FIRST = 'application/json, text/plain, */*';
/**
 * Public (CDN-cacheable) endpoints — e.g. `PublicFlowBuilderRecord`. Mirrors the REST headers helper.
 * For these we must NOT force `cache: 'no-store'`: that makes the browser attach a `Cache-Control` request
 * header and bypass the browser/CDN cache. Public URLs use the default cache mode instead.
 */
const isPublicUrl = (url) => typeof url === 'string' && url.toLowerCase().includes('public');
function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
/** Avoid leaving unread 406 bodies on the wire before retrying (helps some browsers / HTTP stacks). */
async function drainResponseBody(res) {
    try {
        await res.text();
    }
    catch {
        /* ignore */
    }
}
/**
 * GET-style JSON fetch: explicit Accept, **no cache** (avoids stale 406 when DevTools cache is off),
 * and several 406-specific retries with delays (mimics slower execution when Inspector is open).
 */
export async function fetchWithAcceptHeaders(url, init = {}) {
    const merged = {
        ...init,
        // Public URLs stay cacheable (no forced Cache-Control); non-public keep no-store to dodge stale 406s.
        cache: init.cache ?? (isPublicUrl(url) ? 'default' : 'no-store'),
    };
    const fetchOnce = async (accept) => {
        const headers = new Headers(merged.headers ?? undefined);
        headers.set('Accept', accept);
        return fetch(url, { ...merged, headers });
    };
    let res = await fetchOnce(ACCEPT_JSON_FIRST);
    if (res.ok)
        return res;
    // 406: negotiate Accept + spacing — same URL often succeeds on 2nd/3rd try (race / negotiation).
    if (res.status === 406) {
        await drainResponseBody(res);
        await delay(90);
        res = await fetchOnce('*/*');
    }
    if (res.ok)
        return res;
    if (res.status === 406) {
        await drainResponseBody(res);
        await delay(130);
        res = await fetchOnce('application/json');
    }
    if (res.ok)
        return res;
    if (res.status === 406) {
        await drainResponseBody(res);
        await delay(180);
        res = await fetchOnce('*/*');
    }
    return res;
}
/** Flow Builder `PublicFlowBuilderRecord/Grid` — credentials vary (`omit` vs `include`). */
export async function fetchFlowBuilderGrid(url, credentials = 'include') {
    return fetchWithAcceptHeaders(url, { credentials });
}
